import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  CalendarRange,
  Music,
  ListOrdered,
  Users,
  UserCircle,
  MapPin,
  Bed,
  Briefcase,
  Wrench,
  BarChart3,
  Settings,
  LayoutDashboard,
  CalendarPlus,
  LogOut,
  User,
  ChevronRight,
  Route,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { NavDrawer } from './NavDrawer';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  id: string;
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    title: 'Main',
    items: [
      { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { id: 'calendar', name: 'Calendar', href: '/calendar', icon: CalendarRange },
      { id: 'events', name: 'Events', href: '/events', icon: CalendarPlus },
    ],
  },
  {
    title: 'Content',
    items: [
      { id: 'songs', name: 'Songs', href: '/songs', icon: Music },
      { id: 'setlists', name: 'Setlists', href: '/setlists', icon: ListOrdered },
    ],
  },
  {
    title: 'Management',
    items: [
      { id: 'ensembles', name: 'Ensembles', href: '/ensembles', icon: UserCircle },
      { id: 'venues', name: 'Venues', href: '/venues', icon: MapPin },
      { id: 'accommodations', name: 'Accommodations', href: '/accommodations', icon: Bed },
      { id: 'riders', name: 'Riders', href: '/riders', icon: Briefcase },
      { id: 'collaborators', name: 'Collaborators', href: '/collaborators', icon: Users },
    ],
  },
  {
    title: 'Tools',
    items: [
      { id: 'equipment', name: 'Equipment', href: '/equipment', icon: Wrench },
      { id: 'analytics', name: 'Analytics', href: '/analytics', icon: BarChart3 },
      { id: 'ai-tour-planning', name: 'Tour Planning', href: '/ai/tour-planning', icon: Route },
    ],
  },
  {
    title: 'Account',
    items: [
      { id: 'settings', name: 'Settings', href: '/settings', icon: Settings },
    ],
  },
];

export function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const location = useLocation();
  const { logout } = useAuth();

  const handleSignOut = async () => {
    logout();
  };

  const isActive = (href: string) => {
    return location.pathname === href ||
      (href !== '/' && location.pathname.startsWith(href));
  };

  return (
    <NavDrawer isOpen={isOpen} onClose={onClose} side="left">
      <div className="space-y-6">
        <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="w-5 h-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">Luma</p>
            <p className="text-xs text-gray-500 truncate">Tour Management</p>
          </div>
        </div>

        {navigationSections.map((section) => (
          <div key={section.title}>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
              {section.title}
            </h3>
            <nav className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={onClose}
                    className={`
                      flex items-center justify-between w-full px-3 py-3
                      text-sm font-medium rounded-lg
                      transition-colors duration-150
                      min-h-[44px]
                      ${
                        active
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <Icon className={`w-5 h-5 mr-3 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                      <span>{item.name}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                );
              })}
            </nav>
          </div>
        ))}

        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleSignOut}
            className="flex items-center w-full px-3 py-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150 min-h-[44px]"
          >
            <LogOut className="w-5 h-5 mr-3 text-gray-400" />
            <span>Sign out</span>
          </button>
        </div>
      </div>
    </NavDrawer>
  );
}

export default MobileNav;
